import { useState, useEffect, useMemo } from 'react';
import EntitlementCoaches from './team/EntitlementCoaches';
import StandingSlotsPanel from './team/StandingSlotsPanel';
import PackPausesPanel from './team/PackPausesPanel';
import { backdropClose } from '../utils/backdropClose';
import { X, Gem, Calendar, Clock, Trash2, PauseCircle, Ban, FileText, History, Fingerprint, ChevronDown, ShoppingBag, Plus } from 'lucide-react';
import CreatePurchaseModal from './CreatePurchaseModal';
import ProfilePhotoUpload from './ProfilePhotoUpload';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';
import MembershipWizard from './MembershipWizard';
import PauseModal from './PauseModal';
import DeleteMembershipDialog from './DeleteMembershipDialog';
import CancelPurchaseDialog from './CancelPurchaseDialog';
import PauseDetails from './PauseDetails';
import { useUserContext } from '../contexts/UserContext';

// Branch interface removed - branches now managed through membership plans

interface MembershipPlan {
  id: string;
  name: string;
  description: string | null;
  durationMonths: number | null;
  price: number | null;
  isActive: boolean;
  isDefault: boolean;
  features?: any;
}

interface Member {
  id: string;
  name: string;
  displayName: string | null;
  email: string | null;
  phoneNo: string;
  dateOfBirth: string | null;
  gender: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  address: string | null;
  city: string | null;
  pincode: string | null;
  notes: string | null;
  status: string;
  trialEndDate?: string | null;
  memberBranchAssignments: Array<{
    branch: {
      id: string;
      name: string;
      displayName: string;
    };
  }>;
  memberships: Array<{
    id: string;
    plan: MembershipPlan;
    startDate: string;
    endDate: string | null;
    status: string;
    baseAmount?: number | null;
    taxAmount?: number | null;
    taxPercent?: number | null;
    totalAmount?: number | null;
    discountAmount?: number | null;
    discountPercent?: number | null;
    paymentStatus?: string | null;
    paidAmount?: number | null;
    pendingAmount?: number | null;
    dueDate?: string | null;
    pauses?: Array<{
      id: string;
      startDate: string;
      endDate: string | null;
      reason: string | null;
      status: string;
      createdAt?: string;
    }>;
  }>;
  whatsappOptIn?: boolean;
  whatsappOptInAt?: string | null;
  whatsappVerified?: boolean;
  whatsappVerifiedAt?: string | null;
  profilePhoto?: string | null;
  memberProfilePhoto?: string | null;
  gymProfilePhoto?: string | null;
}

interface EditMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: Member | null;
  onMemberUpdated: (updatedMember?: Member) => void;
  readOnly?: boolean;
}

interface AuditLog {
  id: string;
  action: string;
  performedBy: string | null;
  performedAt: string;
  details: any;
  performer?: {
    name: string | null;
    displayName: string | null;
  };
}

interface FormData {
  name: string;
  displayName: string;
  email: string;
  phoneNo: string;
  dateOfBirth: string;
  gender: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  address: string;
  city: string;
  pincode: string;
  notes: string;
  branchId: string; // home branch (management/filtering only)
  whatsappOptIn: boolean;
  whatsappEnabled: boolean;
  termsAccepted: boolean;
}

interface BranchOption {
  id: string;
  name: string;
  displayName: string | null;
}

export default function EditMemberModal({ isOpen, onClose, member, onMemberUpdated, readOnly = false }: EditMemberModalProps) {
  const { userContext } = useUserContext();
  const [loading, setLoading] = useState(false);
  // Branch state removed - managed through membership plans
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'details' | 'memberships' | 'history' | 'attendance' | 'purchases' | 'classes' | 'trainers'>('details');
  const [showCreatePurchaseModal, setShowCreatePurchaseModal] = useState(false);
  // 'plan' when opened via "Buy future plan" (plan-only, future-dated flow).
  const [purchaseMode, setPurchaseMode] = useState<'default' | 'plan'>('default');
  const [gymPurchases, setGymPurchases] = useState<any[]>([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [showMembershipWizard, setShowMembershipWizard] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [membershipToPause, setMembershipToPause] = useState<string | null>(null);
  const [membershipToCancel, setMembershipToCancel] = useState<any | null>(null);
  const [purchaseToCancel, setPurchaseToCancel] = useState<any | null>(null);

  // Attendance History State
  const [attendanceLogs, setAttendanceLogs] = useState<any[]>([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceOffset, setAttendanceOffset] = useState(0);
  const [hasMoreAttendance, setHasMoreAttendance] = useState(true);
  const ATTENDANCE_LIMIT = 5;
  const [formData, setFormData] = useState<FormData>({
    name: '',
    displayName: '',
    email: '',
    phoneNo: '',
    dateOfBirth: '',
    gender: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    address: '',
    city: '',
    pincode: '',
    notes: '',
    branchId: '',
    whatsappOptIn: false,
    whatsappEnabled: false,
    termsAccepted: false,
  });
  const [branches, setBranches] = useState<BranchOption[]>([]);
  const [isWhatsappVerified, setIsWhatsappVerified] = useState(false);
  // Per-category notification subscription (ON = subscribed). Persisted as
  // MemberOptOut rows on the backend; default subscribed to everything.
  const [notifPrefs, setNotifPrefs] = useState<{ transactional: boolean; marketing: boolean }>({ transactional: true, marketing: true });
  const [origNotifPrefs, setOrigNotifPrefs] = useState<{ transactional: boolean; marketing: boolean }>({ transactional: true, marketing: true });
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [gymPhotoUrl, setGymPhotoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Classes tab state
  const [classBookings, setClassBookings] = useState<any[]>([]);
  const [classCredits, setClassCredits] = useState<any[]>([]);
  const [classesLoading, setClassesLoading] = useState(false);

  const currentRole = userContext?.roles.find(r => r.gymId === (member as any)?.gymId || r.gymId === userContext?.accessibleGyms?.[0]);
  const notificationsEnabled = currentRole?.plan?.features?.notificationsEnabled ?? false;


  // Fetch branches and reset UI state
  useEffect(() => {
    if (isOpen) {
      // Branches no longer fetched - managed via plans
      // Reset UI state
      setActiveTab('details');
      setShowMembershipWizard(false);
      setShowPauseModal(false);
      setMembershipToPause(null);
      setShowOtpInput(false);
      setOtp('');
      setIsSendingOtp(false);
      setIsVerifyingOtp(false);
      setOtpError(null);
      // Reset Attendance state for the new member
      setAttendanceLogs([]);
      setAttendanceOffset(0);
      setHasMoreAttendance(true);
      // Sync gym photo from member prop
      setGymPhotoUrl(member?.gymProfilePhoto ?? null);
    }
  }, [isOpen, member]);






  // Generate QR code when member changes
  const generateQRCode = async (phoneNumber: string) => {
    try {
      if (!phoneNumber || phoneNumber.length < 10) {
        console.error('Invalid phone number for QR code:', phoneNumber);
        return;
      }

      const qrCodeDataURL = await QRCode.toDataURL(phoneNumber, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M',
        type: 'image/png'
      });

      setQrCodeUrl(qrCodeDataURL);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const sortedMemberships = useMemo(() => {
    if (!member?.memberships) return [];

    return [...member.memberships].sort((a, b) => {
      // Priority 1: Active
      if (a.status === 'active' && b.status !== 'active') return -1;
      if (b.status === 'active' && a.status !== 'active') return 1;

      // Priority 2: Planned
      if (a.status === 'planned' && b.status !== 'planned') return -1;
      if (b.status === 'planned' && a.status !== 'planned') return 1;

      // Priority 3: Paused
      if (a.status === 'paused' && b.status !== 'paused') return -1;
      if (b.status === 'paused' && a.status !== 'paused') return 1;

      // Everything else: By createdAt desc or startDate desc
      const dateA = new Date((a as any).createdAt || a.startDate || 0).getTime();
      const dateB = new Date((b as any).createdAt || b.startDate || 0).getTime();
      return dateB - dateA;
    });
  }, [member?.memberships]);

  // Latest end date among active/planned memberships. A "future plan" must start
  // after this. Null when the member has no current plan.
  const activePlanEndDate = useMemo<string | null>(() => {
    const ends = (member?.memberships || [])
      .filter((m: any) => (m.status === 'active' || m.status === 'planned') && m.endDate)
      .map((m: any) => m.endDate as string);
    return ends.length ? ends.reduce((a, b) => (a > b ? a : b)) : null;
  }, [member?.memberships]);

  // Only one future (planned) plan is allowed at a time.
  const hasPlannedPlan = useMemo(
    () => (member?.memberships || []).some((m: any) => m.status === 'planned'),
    [member?.memberships]
  );

  const processedLogs = useMemo(() => {
    // 1. Filter out token refreshed items and other system noise
    const filtered = auditLogs.filter(log => {
      const action = (log.action || '').toUpperCase();
      return !['TOKEN_REFRESH', 'TOKEN_REFRESHED', 'SESSION_REFRESH', 'LOGIN', 'LOGOUT'].some(systemAction => action.includes(systemAction));
    });

    // 2. Group by Date
    const groups: { [key: string]: AuditLog[] } = {};
    const monthFirstAppearance: { [key: string]: string } = {};

    filtered.forEach(log => {
      const date = new Date(log.performedAt);
      const dateStr = date.toDateString();
      if (!groups[dateStr]) groups[dateStr] = [];
      groups[dateStr].push(log);

      const year = date.getFullYear();
      const month = date.toLocaleString('default', { month: 'short' });
      const monthKey = `${year}-${month}`;
      if (!monthFirstAppearance[monthKey]) {
        monthFirstAppearance[monthKey] = dateStr;
      }
    });

    // 3. For the sidebar: Group by Year-Month
    const navigation: { year: string; months: string[] }[] = [];
    const navMap: { [year: string]: Set<string> } = {};

    filtered.forEach(log => {
      const date = new Date(log.performedAt);
      const year = date.getFullYear().toString();
      const month = date.toLocaleString('default', { month: 'short' });
      if (!navMap[year]) navMap[year] = new Set();
      navMap[year].add(month);
    });

    Object.keys(navMap).sort((a, b) => b.localeCompare(a)).forEach(year => {
      const months = Array.from(navMap[year]).sort((a, b) => {
        const m = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return m.indexOf(b) - m.indexOf(a);
      });
      navigation.push({ year, months });
    });

    return { filtered, groups, navigation, monthFirstAppearance };
  }, [auditLogs]);

  // Populate form when member changes
  useEffect(() => {
    if (member && isOpen) {
      setFormData({
        name: member.name || '',
        displayName: member.displayName || '',
        email: member.email || '',
        phoneNo: member.phoneNo || '',
        dateOfBirth: member.dateOfBirth ? member.dateOfBirth.split('T')[0] : '',
        gender: member.gender || '',
        emergencyContactName: member.emergencyContactName || '',
        emergencyContactPhone: member.emergencyContactPhone || '',
        address: member.address || '',
        city: member.city || '',
        pincode: member.pincode || '',
        notes: member.notes || '',
        branchId: (member as any).defaultBranchId || '',
        whatsappOptIn: (member as any).whatsappOptIn || false,
        whatsappEnabled: (member as any).whatsappOptIn || false,
        termsAccepted: (member as any).termsAccepted || false,
      });
      setIsWhatsappVerified((member as any).whatsappVerified || false);


      if (member.phoneNo) {
        generateQRCode(member.phoneNo);
      }

      if (member.id) {
        fetchAuditLogs();
      }
    }
  }, [member, isOpen]);

  // Load this member's notification preferences (per-category subscription state).
  useEffect(() => {
    if (!isOpen || !member?.id) return;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const gymId = (member as any).gymId || userContext?.accessibleGyms?.[0];
        const res = await fetch(`${API_BASE_URL}/api/members/${member.id}/notification-preferences${gymId ? `?gymId=${gymId}` : ''}`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });
        if (!res.ok) return;
        const json = await res.json();
        const prefs = {
          transactional: json?.data?.transactional ?? true,
          marketing: json?.data?.marketing ?? true,
        };
        setNotifPrefs(prefs);
        setOrigNotifPrefs(prefs);
      } catch { /* non-blocking — toggles fall back to subscribed */ }
    })();
  }, [member, isOpen]);

  // Load branches for the Home Branch selector.
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const res = await fetch(`${API_BASE_URL}/api/branches`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setBranches((data.data || data.branches || []).filter(
            (b: any) => (b.status ?? 'active') !== 'inactive'
          ));
        }
      } catch {
        /* non-fatal: branch is optional */
      }
    })();
  }, [isOpen]);

  const fetchAttendanceLogs = async (offset = 0) => {
    if (!member) return;
    setAttendanceLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${API_BASE_URL}/api/members/${member.id}/attendance?limit=${ATTENDANCE_LIMIT}&offset=${offset}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      const result = await response.json();
      if (result.success) {
        if (offset === 0) {
          setAttendanceLogs(result.data);
        } else {
          setAttendanceLogs(prev => [...prev, ...result.data]);
        }
        setHasMoreAttendance(result.pagination.hasMore);
        setAttendanceOffset(offset);
      }
    } catch (error) {
      console.error('Error fetching attendance logs:', error);
      toast.error('Failed to load attendance history');
    } finally {
      setAttendanceLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && member && activeTab === 'attendance' && attendanceLogs.length === 0) {
      fetchAttendanceLogs(0);
    }
  }, [isOpen, member, activeTab]);

  const fetchClassData = async () => {
    if (!member) return;
    setClassesLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const headers = { Authorization: `Bearer ${session.access_token}` };
      const gymId = (member as any).gymId;
      const [bookingsRes, creditsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/class-bookings?memberId=${member.id}${gymId ? `&gymId=${gymId}` : ''}`, { headers }),
        fetch(`${API_BASE_URL}/api/class-bookings/credits/${member.id}`, { headers }),
      ]);
      if (bookingsRes.ok) {
        const data = await bookingsRes.json();
        setClassBookings(data.data || []);
      }
      if (creditsRes.ok) {
        const data = await creditsRes.json();
        setClassCredits(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch class data', err);
    } finally {
      setClassesLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && member && activeTab === 'classes' && classBookings.length === 0) {
      fetchClassData();
    }
  }, [isOpen, member, activeTab]);

  const fetchAuditLogs = async () => {
    if (!member) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/api/members/${member.id}/audit`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.data || []);
      } else if (response.status === 404) {
        // Endpoint might not exist yet, that's okay
        setAuditLogs([]);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      setAuditLogs([]);
    }
  };

  const handleSendOtp = async () => {
    setOtp('');
    setOtpError(null);
    if (!formData.phoneNo || formData.phoneNo.length < 10) {
      toast.error('Please enter a valid phone number first');
      return;
    }

    setIsSendingOtp(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${API_BASE_URL}/api/members/whatsapp/send-otp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phoneNumber: formData.phoneNo,
          gymId: (member as any).gymId || (userContext?.accessibleGyms?.[0]),
          memberId: member?.id
        })
      });

      if (response.ok) {
        setShowOtpInput(true);
        toast.success('OTP sent to WhatsApp');
      } else {
        const error = await response.json();
        setOtpError(error.error || 'Failed to send OTP');
        toast.error(error.error || 'Failed to send OTP');
      }
    } catch (error) {
      setOtpError('Error sending OTP');
      toast.error('Error sending OTP');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    setOtpError(null);
    if (otp.length !== 6) {
      toast.error('Please enter a 6-digit OTP');
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${API_BASE_URL}/api/members/whatsapp/verify-otp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phoneNumber: formData.phoneNo,
          gymId: (member as any).gymId || (userContext?.accessibleGyms?.[0]),
          otp
        })
      });

      if (response.ok) {
        setIsWhatsappVerified(true);
        setShowOtpInput(false);
        toast.success('WhatsApp verified successfully');
      } else {
        const error = await response.json();
        setOtpError(error.error || 'Invalid OTP');
        toast.error(error.error || 'Invalid OTP');
      }
    } catch (error) {
      setOtpError('Error verifying OTP');
      toast.error('Error verifying OTP');
    } finally {
      setIsVerifyingOtp(false);
    }
  };


  const isIndianPhoneNumber = (phone: string): boolean => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone.replace(/\D/g, ''));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return false;
    }

    if (!formData.phoneNo.trim()) {
      toast.error('Phone number is required');
      return false;
    }

    if (!isIndianPhoneNumber(formData.phoneNo)) {
      toast.error('Please enter a valid 10-digit Indian phone number');
      return false;
    }

    if (formData.dateOfBirth) {
      const dob = new Date(formData.dateOfBirth);
      const today = new Date();
      if (dob > today) {
        toast.error('Date of birth cannot be a future date');
        return false;
      }
      const age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (age < 14 || (age === 14 && monthDiff < 0)) {
        toast.error('Member must be at least 14 years old');
        return false;
      }
    }

    // Emergency contact validation - optional but must be valid if provided
    if (formData.emergencyContactName && formData.emergencyContactName.trim()) {
      if (formData.emergencyContactName.trim().length < 2) {
        toast.error('Emergency contact name must be at least 2 characters');
        return false;
      }
      if (!/^[a-zA-Z\s.'-]+$/.test(formData.emergencyContactName.trim())) {
        toast.error('Emergency contact name can only contain letters, spaces, and basic punctuation');
        return false;
      }
    }

    if (formData.emergencyContactPhone && formData.emergencyContactPhone.trim()) {
      if (!isIndianPhoneNumber(formData.emergencyContactPhone)) {
        toast.error('Please enter a valid 10-digit Indian phone number for emergency contact');
        return false;
      }
      if (formData.emergencyContactPhone === formData.phoneNo) {
        toast.error('Emergency contact number cannot be the same as member number');
        return false;
      }
    }

    if (formData.address && formData.address.trim()) {
      if (formData.address.trim().length < 5) {
        toast.error('Address must be at least 5 characters');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!member) return;

    if (!validateForm()) return;

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Not authenticated');
        return;
      }

      const payload = {
        ...formData,
        dateOfBirth: formData.dateOfBirth || null,
        gymId: (member as any).gymId || userContext?.accessibleGyms?.[0],
        notificationPrefs: notifPrefs
      };

      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/api/members/${member.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Member updated successfully');

        // Update local form data with the response
        if (result.data) {
          const updatedData = {
            name: result.data.name || '',
            displayName: result.data.displayName || '',
            email: result.data.email || '',
            phoneNo: result.data.phoneNo || '',
            dateOfBirth: result.data.dateOfBirth ? result.data.dateOfBirth.split('T')[0] : '',
            gender: result.data.gender || '',
            emergencyContactName: result.data.emergencyContactName || '',
            emergencyContactPhone: result.data.emergencyContactPhone || '',
            address: result.data.address || '',
            city: result.data.city || '',
            pincode: result.data.pincode || '',
            notes: result.data.notes || '',
            branchId: formData.branchId, // preserve; PUT response omits the mapping's home branch
            whatsappOptIn: result.data.whatsappOptIn || false,
            whatsappEnabled: result.data.whatsappOptIn || false,
            termsAccepted: result.data.termsAccepted || false,
          };
          setFormData(updatedData);
          setOrigNotifPrefs(notifPrefs); // saved — reset the change baseline
          // Immediately propagate updated member to parent so list + modal stay in sync
          onMemberUpdated({ ...member, ...result.data });
        }

        // Refresh audit logs
        fetchAuditLogs();
      } else {
        const errorData = await response.json();

        if (errorData.suggestRestore && errorData.memberId) {
          toast((t) => (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">{errorData.error}</p>
              <button
                onClick={async () => {
                  toast.dismiss(t.id);
                  await handleRestore(errorData.memberId);
                }}
                className="px-3 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-colors"
              >
                Restore Member Now
              </button>
            </div>
          ), { duration: 6000, icon: '💡' });
        } else {
          toast.error(errorData.error || 'Failed to update member');
        }
      }
    } catch (error) {
      console.error('Error updating member:', error);
      toast.error('Failed to update member');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (memberIdToRestore: string) => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${API_BASE_URL}/api/members/${memberIdToRestore}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Member restored successfully');
        onMemberUpdated(memberIdToRestore as any);
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to restore member');
      }
    } catch (error) {
      toast.error('Error restoring member');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (blob: Blob) => {
    if (!member) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('photo', blob, 'profile.jpg');

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await fetch(`${API_BASE_URL}/api/members/${member.id}/profile-photo`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');

      setGymPhotoUrl(data.gymProfilePhoto);
      toast.success('Profile photo updated');
      onMemberUpdated({ ...member, gymProfilePhoto: data.gymProfilePhoto, profilePhoto: data.gymProfilePhoto });
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleConvertToTrial = async () => {
    if (!member) return;

    const days = window.prompt('Enter trial duration in days (1-5):', '3');
    if (days === null) return; // Cancelled

    const trialDays = parseInt(days);
    if (isNaN(trialDays) || trialDays < 1 || trialDays > 5) {
      toast.error('Trial duration must be between 1 and 5 days');
      return;
    }

    const confirm = window.confirm(`Are you sure you want to convert this lead to a ${trialDays}-day trial member?`);
    if (!confirm) return;

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Not authenticated');
        return;
      }

      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/api/members/${member.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          status: 'trial',
          trialDays: trialDays.toString()
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Member converted to trial successfully');
        onMemberUpdated(data.data); // Immediate UI update
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to convert to trial');
      }
    } catch (error) {
      console.error('Error converting to trial:', error);
      toast.error('Failed to convert to trial');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'phoneNo' && value !== (member as any)?.phoneNo) {
      setIsWhatsappVerified(false);
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // handleBranchToggle removed - branches managed through membership plans

  const hasChanges = useMemo(() => {
    if (!member) return false;

    // Check simple fields
    if (formData.name !== (member.name || '')) return true;
    if (formData.displayName !== (member.displayName || '')) return true;
    if (formData.email !== (member.email || '')) return true;
    if (formData.phoneNo !== (member.phoneNo || '')) return true;

    const dob = member.dateOfBirth ? member.dateOfBirth.split('T')[0] : '';
    if (formData.dateOfBirth !== dob) return true;

    if (formData.gender !== (member.gender || '')) return true;
    if (formData.emergencyContactName !== (member.emergencyContactName || '')) return true;
    if (formData.emergencyContactPhone !== (member.emergencyContactPhone || '')) return true;
    if (formData.address !== (member.address || '')) return true;
    if (formData.city !== (member.city || '')) return true;
    if (formData.pincode !== (member.pincode || '')) return true;
    if (formData.notes !== (member.notes || '')) return true;

    // Check whatsapp opt-in (converting member property to boolean safely)
    const originalWhatsAppOptIn = (member as any).whatsappOptIn || false;
    if (formData.whatsappOptIn !== originalWhatsAppOptIn) return true;

    // Notification preferences (per-category subscription).
    if (notifPrefs.transactional !== origNotifPrefs.transactional) return true;
    if (notifPrefs.marketing !== origNotifPrefs.marketing) return true;

    // Home branch (per-gym mapping) — changing it should enable Save.
    if (formData.branchId !== ((member as any).defaultBranchId || '')) return true;

    return false;
  }, [formData, member, notifPrefs, origNotifPrefs]);

  const [showQRModal, setShowQRModal] = useState(false);

  // Check if verify otp logic needs update regarding the state insertion...
  // I will just insert the state at the top. Wait, let's do this separately.

  if (!isOpen || !member) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[1000] p-0 sm:p-6 pb-safe animate-in fade-in duration-300" {...backdropClose(onClose)}>
      {showQRModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4" {...backdropClose(() => setShowQRModal(false))}>
          <div className="bg-white rounded-xl p-6 max-w-sm w-full flex flex-col items-center space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-gray-900">Member QR Code</h3>
            <div className="bg-white p-2 rounded-lg border-2 border-gray-100">
              <img src={qrCodeUrl} alt="QR Code" className="w-64 h-64" />
            </div>
            <p className="text-sm text-center text-gray-500 font-medium">{member.displayName || member.name}</p>
            <div className="flex w-full gap-3 pt-2">
              <button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = qrCodeUrl;
                  link.download = `gympe-qr-${member.phoneNo}.png`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  toast.success('QR Code downloaded!');
                }}
                className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </button>
              <button
                onClick={() => setShowQRModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      <div className="bg-white rounded-t-[2rem] sm:rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92dvh] sm:max-h-[90dvh] overflow-hidden flex flex-col pb-safe animate-in slide-in-from-bottom duration-500" onClick={(e) => e.stopPropagation()}>

        {/* ── Compact header ── */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-4 py-2.5 text-white shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {/* Avatar */}
            <div className="shrink-0">
              <ProfilePhotoUpload
                currentPhotoUrl={gymPhotoUrl}
                fallbackPhotoUrl={member.memberProfilePhoto ?? null}
                onUpload={handlePhotoUpload}
                uploading={uploadingPhoto}
                initials={(member.displayName || member.name || '?').charAt(0)}
                size={36}
                readOnly={readOnly}
                variant="light"
              />
            </div>

            {/* Name + status + phone */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <h2 className="text-sm font-extrabold tracking-tight leading-none truncate">
                  {member.displayName || member.name || 'Member'}
                </h2>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider shrink-0 ${
                  member.status === 'trial' ? 'bg-green-400 text-green-950' :
                  member.status === 'lead'  ? 'bg-yellow-400 text-yellow-950' :
                  'bg-blue-400 text-blue-950'}`}>
                  {member.status === 'trial' ? (() => {
                    const d = member.trialEndDate ? Math.ceil((new Date(member.trialEndDate).getTime() - Date.now()) / 86400000) : null;
                    return d === null ? 'Trial' : d >= 0 ? `Trial (${d}d)` : 'Trial Expired';
                  })() : member.status}
                </span>
                {readOnly && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-white/20 shrink-0">View Only</span>
                )}
              </div>
              <p className="text-[11px] text-blue-100 mt-0.5">{member.phoneNo}</p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1.5 shrink-0">
              {!readOnly && member.status === 'lead' && (
                <button
                  onClick={handleConvertToTrial}
                  disabled={loading}
                  className="bg-white/15 hover:bg-white/25 border border-white/20 px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all active:scale-95 disabled:opacity-50"
                >
                  {loading ? 'Converting…' : 'Convert to Trial'}
                </button>
              )}
              {qrCodeUrl && (
                <button
                  onClick={() => setShowQRModal(true)}
                  className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all border border-white/20 active:scale-95"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h2M4 8h12m4 0h2M4 16h4m12 0h2M4 20h12" />
                  </svg>
                  QR
                </button>
              )}
              <button
                onClick={onClose}
                className="p-1.5 bg-white/10 hover:bg-white/25 rounded-lg transition-all active:scale-95"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* ── Compact tabs ── */}
        <div className="border-b border-gray-100 bg-white shrink-0">
          <div className="flex overflow-x-auto px-3 gap-0.5 no-scrollbar">
            {[
              { id: 'details',     label: 'Details' },
              { id: 'memberships', label: 'Memberships' },
              { id: 'purchases',   label: 'Purchases' },
              { id: 'classes',     label: 'Bookings' },
              { id: 'trainers',    label: 'Trainers' },
              { id: 'attendance',  label: 'Check-ins' },
              { id: 'history',     label: 'Audit' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                data-tab={tab.id}
                className={`px-3 py-2 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors shrink-0 ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-400 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {/* Tab Content Area - Flexible Responsive Height */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className={`flex-1 flex flex-col ${(showMembershipWizard && activeTab === 'memberships') || activeTab === 'history' ? 'overflow-hidden p-0 sm:p-0' : 'overflow-y-auto p-4 sm:p-6 pb-24 sm:pb-6'} scroll-smooth custom-scrollbar`}>

            {/* ── Classes Tab ── */}
            {activeTab === 'classes' && (
              <ClassesTab
                bookings={classBookings}
                credits={classCredits}
                loading={classesLoading}
                onRefresh={fetchClassData}
              />
            )}

            {/* ── Trainers Tab ── */}
            {activeTab === 'trainers' && member && (
              <TrainersTab memberId={member.id} gymId={(member as any).gymId || userContext?.accessibleGyms?.[0] || ''} readOnly={readOnly} />
            )}

            {/* ── Purchases Tab ── */}
            {activeTab === 'purchases' && (
              <PurchasesTab
                member={member}
                gymPurchases={gymPurchases}
                setGymPurchases={setGymPurchases}
                purchasesLoading={purchasesLoading}
                setPurchasesLoading={setPurchasesLoading}
                onAddPurchase={() => { setPurchaseMode('default'); setShowCreatePurchaseModal(true); }}
                setPurchaseToCancel={setPurchaseToCancel}
              />
            )}

            {activeTab === 'details' && (
              <form onSubmit={handleSubmit}>
                <fieldset disabled={readOnly} className="space-y-0">

                  {/* ── Profile photo ── */}
                  <div className="flex items-center gap-4 pb-4 mb-4 border-b border-gray-100">
                    <ProfilePhotoUpload
                      currentPhotoUrl={gymPhotoUrl}
                      fallbackPhotoUrl={member.memberProfilePhoto ?? null}
                      onUpload={handlePhotoUpload}
                      uploading={uploadingPhoto}
                      initials={(member.displayName || member.name || '?').charAt(0)}
                      size={60}
                      readOnly={readOnly}
                      variant="light"
                    />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{member.displayName || member.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{readOnly ? 'Profile photo' : 'Click photo to update'}</p>
                    </div>
                  </div>

                  {/* ── Personal ── */}
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Personal</p>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                    <div className="col-span-2 sm:col-span-1 space-y-1">
                      <label className="block text-xs font-semibold text-gray-500">Full Name *</label>
                      <input type="text" name="name" value={formData.name} onChange={handleInputChange} required
                        className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div className="col-span-2 sm:col-span-1 space-y-1">
                      <label className="block text-xs font-semibold text-gray-500">Display Name</label>
                      <input type="text" name="displayName" value={formData.displayName} onChange={handleInputChange}
                        className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div className="col-span-2 sm:col-span-1 space-y-1">
                      <label className="block text-xs font-semibold text-gray-500">Phone Number *</label>
                      <input type="tel" name="phoneNo" value={formData.phoneNo} onChange={handleInputChange} required
                        className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div className="col-span-2 sm:col-span-1 space-y-1">
                      <label className="block text-xs font-semibold text-gray-500">Email</label>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                        className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-xs font-semibold text-gray-500 flex items-center gap-1">
                        Date of Birth
                        <span className="group relative">
                          <svg className="w-3 h-3 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block w-44 p-2 bg-gray-800 text-white text-[10px] rounded shadow-lg z-20">
                            Members below 14 must be under parent supervision.
                          </div>
                        </span>
                      </label>
                      <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleInputChange}
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-xs font-semibold text-gray-500">Gender</label>
                      <select name="gender" value={formData.gender} onChange={handleInputChange}
                        className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all">
                        <option value="">—</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                  </div>

                  {/* ── Emergency Contact ── */}
                  <div className="border-t border-gray-100 mt-4 pt-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Emergency Contact</p>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                      <div className="space-y-1">
                        <label className="block text-xs font-semibold text-gray-500">Name</label>
                        <input type="text" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleInputChange}
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-xs font-semibold text-gray-500">Phone</label>
                        <input type="tel" name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={handleInputChange}
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                      </div>
                    </div>
                  </div>

                  {/* ── Address ── */}
                  <div className="border-t border-gray-100 mt-4 pt-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Address</p>
                    <div className="space-y-3">
                      <textarea name="address" value={formData.address} onChange={handleInputChange} rows={2}
                        placeholder="Street address"
                        className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none" />
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="block text-xs font-semibold text-gray-500">City</label>
                          <input type="text" name="city" value={formData.city || ''} onChange={handleInputChange}
                            className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                        </div>
                        <div className="space-y-1">
                          <label className="block text-xs font-semibold text-gray-500">Pincode</label>
                          <input type="text" name="pincode" value={formData.pincode} onChange={handleInputChange}
                            className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Notes ── */}
                  <div className="border-t border-gray-100 mt-4 pt-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Notes</p>
                    <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={2}
                      placeholder="Any notes about this member..."
                      className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none" />
                  </div>

                  {/* ── Home Branch ── */}
                  {branches.length > 1 && (
                    <div className="border-t border-gray-100 mt-4 pt-4">
                      <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Home Branch</p>
                      <select name="branchId" value={formData.branchId} onChange={handleInputChange} disabled={readOnly}
                        className="w-full px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all disabled:bg-gray-50">
                        <option value="">No home branch</option>
                        {branches.map(b => (
                          <option key={b.id} value={b.id}>{b.displayName || b.name}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                        Used to manage and filter this member by branch. Check-in access is still
                        set by the member's active membership plans.
                      </p>
                    </div>
                  )}

                  {/* ── Consents ── */}
                  {(member as any).termsAcceptedAt && (
                    <div className="border-t border-gray-100 mt-4 pt-3 flex items-center gap-2">
                      <svg className="w-3.5 h-3.5 text-emerald-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <p className="text-xs text-emerald-700">
                        Terms accepted on {new Date((member as any).termsAcceptedAt).toLocaleDateString()}
                      </p>
                    </div>
                  )}

                  {/* ── WhatsApp ── */}
                  <div className="border-t border-gray-100 mt-4 pt-4">
                    <div className="flex items-center gap-3">
                      <div className="p-1 bg-emerald-500 rounded-md shrink-0">
                        <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-700 leading-none">WhatsApp Notifications</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">Check-in alerts, reminders & updates</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isWhatsappVerified && (
                          <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Verified
                          </span>
                        )}
                        <button type="button" onClick={handleSendOtp} disabled={isSendingOtp || !notificationsEnabled}
                          className="text-[11px] font-bold text-blue-600 hover:text-blue-700 disabled:opacity-50">
                          {isSendingOtp ? 'Sending…' : (isWhatsappVerified ? 'Re-verify' : 'Verify')}
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 space-y-2">
                      {/* Category toggles — ON = subscribed, OFF = opted out */}
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-700 leading-none">Transactional</p>
                          <p className="text-[11px] text-gray-400 mt-0.5">Receipts, reminders, bookings</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                          <input type="checkbox" checked={notifPrefs.transactional}
                            onChange={(e) => setNotifPrefs(p => ({ ...p, transactional: e.target.checked }))}
                            disabled={readOnly} className="sr-only peer" />
                          <div className={`relative w-9 h-5 rounded-full transition-all ${notifPrefs.transactional ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                            <span className={`absolute top-[2px] left-[2px] h-4 w-4 rounded-full bg-white border border-gray-300 transition-transform ${notifPrefs.transactional ? 'translate-x-4' : ''}`} />
                          </div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-700 leading-none">Marketing</p>
                          <p className="text-[11px] text-gray-400 mt-0.5">Offers, broadcasts, birthday wishes</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                          <input type="checkbox" checked={notifPrefs.marketing}
                            onChange={(e) => setNotifPrefs(p => ({ ...p, marketing: e.target.checked }))}
                            disabled={readOnly} className="sr-only peer" />
                          <div className={`relative w-9 h-5 rounded-full transition-all ${notifPrefs.marketing ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                            <span className={`absolute top-[2px] left-[2px] h-4 w-4 rounded-full bg-white border border-gray-300 transition-transform ${notifPrefs.marketing ? 'translate-x-4' : ''}`} />
                          </div>
                        </label>
                      </div>

                        {showOtpInput && (
                          <div className="p-3 bg-white rounded-lg border border-blue-100 space-y-3 animate-in fade-in slide-in-from-top-1">
                            <div className="text-center">
                              <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">Enter 6-digit code</p>
                              <p className="text-[11px] text-gray-400">Sent to {formData.phoneNo}</p>
                            </div>
                            <input type="text" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-lg font-black tracking-[0.4em] text-blue-600"
                              placeholder="000000" />
                            <div className="flex gap-2">
                              <button type="button" onClick={handleVerifyOtp} disabled={isVerifyingOtp || otp.length !== 6}
                                className="flex-1 px-3 py-2 bg-emerald-600 text-white text-sm rounded-lg font-bold hover:bg-emerald-700 transition-all active:scale-95 disabled:opacity-50">
                                {isVerifyingOtp ? 'Verifying…' : '✓ Verify'}
                              </button>
                              <button type="button" onClick={() => { setShowOtpInput(false); setOtp(''); setOtpError(null); }}
                                className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg font-bold hover:bg-gray-200 transition-all active:scale-95">
                                Cancel
                              </button>
                            </div>
                            {otpError && (
                              <p className="text-xs text-red-500 font-bold bg-red-50 px-3 py-1.5 rounded-lg border border-red-100 text-center">{otpError}</p>
                            )}
                          </div>
                        )}
                      </div>
                  </div>

                </fieldset>
              </form>
            )}

            {activeTab === 'memberships' && (
              <div className={`flex flex-col flex-1 min-h-0 ${!showMembershipWizard ? 'space-y-6' : ''}`}>
                {!showMembershipWizard && (
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">Memberships</h3>
                    {member.status === 'deleted' ? (
                      <span className="text-red-600 font-medium text-sm flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        Restore member to add membership
                      </span>
                    ) : !readOnly ? (
                      <div className="flex items-center gap-2">
                        {activePlanEndDate && !hasPlannedPlan && (
                          <button
                            onClick={() => { setPurchaseMode('plan'); setShowCreatePurchaseModal(true); }}
                            className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors flex items-center space-x-2 border border-emerald-200"
                            title="Buy the next plan, starting after the current one ends"
                          >
                            <Calendar className="w-4 h-4" />
                            <span>Buy Future Plan</span>
                          </button>
                        )}
                        <button
                          onClick={() => setActiveTab('purchases')}
                          className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors flex items-center space-x-2 border border-indigo-200"
                        >
                          <ShoppingBag className="w-4 h-4" />
                          <span>Add via Purchases</span>
                        </button>
                      </div>
                    ) : null}
                  </div>
                )}

                {showMembershipWizard ? (
                  <div className="flex-1 flex flex-col min-h-0">
                    <MembershipWizard
                      memberId={member.id}
                      onSuccess={() => {
                        setShowMembershipWizard(false);
                        onMemberUpdated(); // Refresh member data
                      }}
                      onCancel={() => setShowMembershipWizard(false)}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sortedMemberships.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <FileText className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-gray-600 mb-4 font-bold">no memberships found</p>
                        <p className="text-[10px] text-gray-500 mb-6 font-medium lowercase">start by adding a new plan for this member.</p>
                        {member.status === 'deleted' ? (
                          <div className="px-4 py-2 bg-red-50 text-red-700 rounded-xl inline-flex items-center gap-2 border border-red-100">
                            <Ban className="w-4 h-4" />
                            <span className="text-[10px] font-black uppercase tracking-wider">restore user to add plan</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <p className="text-xs text-gray-500 mb-4 px-6">
                              Membership plans are now managed via the unified purchase system.
                            </p>
                            <button
                              onClick={() => setActiveTab('purchases')}
                              className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 active:scale-95 flex items-center gap-2"
                            >
                              <ShoppingBag className="w-4 h-4" />
                              <span>Go to Purchases Tab</span>
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Active & Planned Section */}
                        {sortedMemberships.filter(m => m.status === 'active' || m.status === 'planned' || m.status === 'paused').map((membership: any) => {
                          const basePrice = Number(membership.baseAmount || membership.plan.price || 0);
                          const totalDiscount = membership.discountAmount !== null && membership.discountAmount !== undefined
                            ? Number(membership.discountAmount)
                            : (basePrice * Number(membership.discountPercent || 0)) / 100;

                          const subtotal = Math.max(0, basePrice - totalDiscount);
                          const taxPercent = Number(membership.taxPercent || 18);
                          const taxAmount = Number(membership.taxAmount || (subtotal * taxPercent) / 100);
                          const totalAmount = Number(membership.totalAmount || (subtotal + taxAmount));

                          const now = new Date();
                          const startDate = new Date(membership.startDate);
                          const endDate = membership.endDate ? new Date(membership.endDate) : null;
                          const isCurrentlyActive = startDate <= now && (!endDate || endDate >= now);

                          // Rule: Cannot pause active if a planned membership exists. 
                          // Rule: CAN pause planned membership itself.
                          const hasPlannedMembership = sortedMemberships.some(m => m.status === 'planned');
                          const isPlanPauseEnabled = membership.plan.features?.pauseEnabled;

                          let canPause = false;
                          if (membership.status === 'active') {
                            canPause = isCurrentlyActive && isPlanPauseEnabled && !hasPlannedMembership;
                          } else if (membership.status === 'planned') {
                            canPause = isPlanPauseEnabled;
                          }

                          const isActive = membership.status === 'active';
                          const isPlanned = membership.status === 'planned';

                          return (
                            <div key={membership.id} className={`relative overflow-hidden group transition-all duration-300 rounded-3xl border ${isActive ? 'bg-gradient-to-br from-blue-50 to-white border-blue-200 shadow-xl shadow-blue-500/5' :
                              isPlanned ? 'bg-gradient-to-br from-amber-50 to-white border-amber-200' :
                                'bg-white border-gray-200'
                              } p-5 sm:p-6`}>

                              {/* Decorative background elements */}
                              {isActive && <Gem className="absolute -right-4 -top-4 w-24 h-24 text-blue-500/5 rotate-12" />}
                              {isPlanned && <Clock className="absolute -right-4 -top-4 w-24 h-24 text-amber-500/5 rotate-12" />}

                              <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div className="flex items-start gap-4">
                                  <div className={`shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center ${isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' :
                                    isPlanned ? 'bg-amber-500 text-white shadow-lg shadow-amber-100' :
                                      'bg-gray-100 text-gray-400'
                                    }`}>
                                    {isActive ? <Gem className="w-6 h-6" /> : isPlanned ? <Clock className="w-6 h-6" /> : < Ban className="w-6 h-6" />}
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2 mb-1">
                                      <h3 className="text-lg font-black text-gray-900 tracking-tight uppercase">{membership.plan.name}</h3>
                                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${isActive ? 'bg-blue-600 text-white' :
                                        isPlanned ? 'bg-amber-100 text-amber-700' :
                                          'bg-gray-100 text-gray-600'
                                        }`}>
                                        {membership.status}
                                      </span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-4 text-[10px] text-gray-500 font-bold uppercase tracking-wide">
                                      <div className="flex items-center gap-1.5">
                                        <Calendar className="w-3 h-3 text-gray-400" />
                                        <span>{new Date(membership.startDate).toLocaleDateString()} — {membership.endDate ? new Date(membership.endDate).toLocaleDateString() : 'no expiry'}</span>
                                      </div>
                                      <div className="flex items-center gap-1.5">
                                        <Clock className="w-3 h-3 text-gray-400" />
                                        <span>{membership.plan.durationMonths || '—'} months</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between sm:justify-end gap-6 sm:gap-8 pt-4 sm:pt-0 border-t sm:border-t-0 border-gray-100 sm:border-none">
                                  <div className="text-right">
                                    {(() => {
                                      const ps = membership.paymentStatus;
                                      const paid = membership.paidAmount != null ? Number(membership.paidAmount) : totalAmount;
                                      const pending = Number(membership.pendingAmount || 0);
                                      const isOverdue = (ps === 'due' || ps === 'partial') && membership.dueDate && new Date(membership.dueDate) < new Date();
                                      const hasDue = ps === 'due' || ps === 'partial';
                                      if (hasDue) {
                                        return (
                                          <>
                                            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">amount paid</div>
                                            <div className={`text-xl font-black ${isActive ? 'text-blue-700' : 'text-gray-900'}`}>₹{paid.toLocaleString()}</div>
                                            <div className="text-[10px] text-gray-400 font-medium mt-0.5">of ₹{totalAmount.toLocaleString()}</div>
                                            <div className={`mt-1 px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-1 ${isOverdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                              <span className={`w-1.5 h-1.5 rounded-full ${isOverdue ? 'bg-red-500 animate-pulse' : 'bg-amber-500'}`}></span>
                                              {isOverdue ? 'Overdue' : 'Due'} ₹{pending.toLocaleString()}
                                            </div>
                                          </>
                                        );
                                      }
                                      return (
                                        <>
                                          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">amount paid</div>
                                          <div className={`text-xl font-black ${isActive ? 'text-blue-700' : 'text-gray-900'}`}>₹{totalAmount.toLocaleString()}</div>
                                        </>
                                      );
                                    })()}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {canPause && (
                                      <button
                                        onClick={() => { setMembershipToPause(membership.id); setShowPauseModal(true); }}
                                        className="p-2.5 bg-amber-50 text-amber-600 hover:bg-amber-100 rounded-xl transition-all active:scale-90"
                                      >
                                        <PauseCircle className="w-5 h-5" />
                                      </button>
                                    )}
                                    {!readOnly && (
                                      <button
                                        onClick={() => {
                                          const basePrice = Number(membership.baseAmount || membership.plan.price || 0);
                                          const totalDiscount = membership.discountAmount !== null && membership.discountAmount !== undefined
                                            ? Number(membership.discountAmount)
                                            : (basePrice * Number(membership.discountPercent || 0)) / 100;

                                          const subtotal = Math.max(0, basePrice - totalDiscount);
                                          const taxPercent = Number(membership.taxPercent || 18);
                                          const taxAmount = Number(membership.taxAmount || (subtotal * taxPercent) / 100);
                                          const totalAmount = Number(membership.totalAmount || (subtotal + taxAmount));

                                          setMembershipToCancel({
                                            ...membership,
                                            baseAmount: basePrice,
                                            discountAmount: totalDiscount,
                                            taxAmount,
                                            taxPercent,
                                            totalAmount
                                          });
                                        }}
                                        className="p-2.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl transition-all active:scale-90"
                                      >
                                        <Trash2 className="w-5 h-5" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Pause Details Integrated */}
                              {membership.pauses && membership.pauses.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-gray-100 animate-in fade-in duration-500">
                                  <PauseDetails
                                    membershipId={membership.id}
                                    onPauseDeleted={() => onMemberUpdated()}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* History Section - Minimalized */}
                        {sortedMemberships.filter(m => m.status !== 'active' && m.status !== 'planned' && m.status !== 'paused').length > 0 && (
                          <div className="pt-4 space-y-3">
                            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2 flex items-center gap-2 mb-2">
                              Historical Records
                              <div className="h-px bg-gray-200 flex-1"></div>
                            </h4>
                            <div className="bg-gray-50/50 rounded-2xl border border-gray-100 overflow-hidden">
                              {sortedMemberships.filter(m => m.status !== 'active' && m.status !== 'planned' && m.status !== 'paused').map((membership: any, idx, arr) => (
                                <div key={membership.id} className={`flex items-center justify-between p-3 sm:px-6 hover:bg-white transition-colors group ${idx !== arr.length - 1 ? 'border-b border-gray-100' : ''}`}>
                                  <div className="flex items-center gap-3">
                                    <div className="p-1.5 bg-gray-200/50 rounded-lg text-gray-400 group-hover:bg-gray-200 group-hover:text-gray-600 transition-colors">
                                      <FileText className="w-3.5 h-3.5" />
                                    </div>
                                    <div>
                                      <div className="text-[11px] font-bold text-gray-700 uppercase tracking-tight line-clamp-1">{membership.plan.name}</div>
                                      <div className="text-[9px] font-medium text-gray-400 lowercase">{new Date(membership.startDate).toLocaleDateString()} — {membership.endDate ? new Date(membership.endDate).toLocaleDateString() : 'no expiry'}</div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wider ${membership.status === 'cancelled' ? 'bg-red-50 text-red-600' :
                                      membership.status === 'refund' ? 'bg-blue-50 text-blue-600' :
                                        'bg-gray-200 text-gray-600'}`}>
                                      {membership.status}
                                    </span>
                                    <div className="flex flex-col items-end">
                                      {(() => {
                                        const isCancelledOrRefund = membership.status === 'cancelled' || membership.status === 'refund';
                                        const ps = membership.paymentStatus;
                                        const paid = membership.paidAmount != null ? Number(membership.paidAmount) : Number(membership.totalAmount || 0);
                                        const pending = Number(membership.pendingAmount || 0);
                                        const hadPartial = !isCancelledOrRefund && (ps === 'due' || ps === 'partial') && pending > 0;
                                        return (
                                          <>
                                            <div className="text-[11px] font-black text-gray-600 italic">
                                              ₹{(isCancelledOrRefund ? paid : Number(membership.totalAmount || 0)).toLocaleString()}
                                            </div>
                                            {hadPartial && (
                                              <div className="text-[9px] font-bold text-amber-600">₹{pending.toLocaleString()} pending</div>
                                            )}
                                            {membership.status === 'refund' && (membership as any).refundAmount && (
                                              <div className="text-[9px] font-bold text-red-500 italic">-₹{Number((membership as any).refundAmount).toLocaleString()}</div>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}




            {activeTab === 'attendance' && (
              <div className="p-4 sm:p-8 space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                    <span className="p-2 bg-blue-50 rounded-lg">
                      <Fingerprint className="w-5 h-5 text-blue-600" />
                    </span>
                    Check-in History
                  </h3>
                  <div className="px-3 py-1 bg-gray-100 rounded-full">
                    <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                      Total Sessions: {attendanceLogs.length}
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  {attendanceLogs.length === 0 && !attendanceLoading ? (
                    <div className="text-center py-20 bg-gray-50/50 rounded-3xl border-2 border-dashed border-gray-200">
                      <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto mb-4">
                        <Fingerprint className="w-8 h-8 text-gray-300" />
                      </div>
                      <p className="text-gray-500 font-medium">No check-in history found</p>
                    </div>
                  ) : (
                    <div className="grid gap-3">
                      {attendanceLogs.map((log) => {
                        const checkIn = new Date(log.checkInTime);
                        const checkOut = log.checkOutTime ? new Date(log.checkOutTime) : null;

                        // Calculate duration
                        let durationStr = 'In Progress';
                        if (checkOut) {
                          const diffMs = checkOut.getTime() - checkIn.getTime();
                          const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
                          const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                          durationStr = diffHrs > 0
                            ? `${diffHrs}h ${diffMins}m`
                            : `${diffMins} mins`;
                        }

                        return (
                          <div key={log.id} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-all group">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                              <div className="flex items-center gap-4">
                                <div className="hidden sm:flex flex-col items-center justify-center w-12 h-12 bg-gray-50 rounded-xl border border-gray-100">
                                  <span className="text-[10px] font-black text-gray-400 uppercase leading-none">{checkIn.toLocaleString('default', { month: 'short' })}</span>
                                  <span className="text-lg font-black text-gray-900 leading-none">{checkIn.getDate()}</span>
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-black text-blue-600 uppercase tracking-tight bg-blue-50 px-2 py-0.5 rounded">
                                      {log.branch?.displayName || log.branch?.name || 'Primary Branch'}
                                    </span>
                                    <span className="text-[10px] font-bold text-gray-400 uppercase">
                                      {checkIn.toLocaleDateString(undefined, { weekday: 'long' })}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <div className="flex flex-col">
                                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Check-in</span>
                                      <span className="text-sm font-bold text-gray-900 tabular-nums">
                                        {checkIn.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    </div>
                                    <div className="h-8 w-px bg-gray-100"></div>
                                    <div className="flex flex-col">
                                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Check-out</span>
                                      <span className={`text-sm font-bold tabular-nums ${checkOut ? 'text-gray-900' : 'text-emerald-500 animate-pulse'}`}>
                                        {checkOut ? checkOut.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Active'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center gap-6">
                                <div className="flex flex-col items-end">
                                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Duration</span>
                                  <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full ${checkOut ? 'bg-gray-100 text-gray-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                    <Clock className="w-3 h-3" />
                                    <span className="text-xs font-black tracking-tight">{durationStr}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {hasMoreAttendance && (
                    <div className="mt-8 flex justify-center">
                      <button
                        type="button"
                        onClick={() => fetchAttendanceLogs(attendanceOffset + ATTENDANCE_LIMIT)}
                        disabled={attendanceLoading}
                        className="flex items-center gap-2 px-8 py-3 bg-white border border-gray-200 rounded-2xl text-sm font-bold text-gray-700 hover:bg-gray-50 hover:border-blue-200 hover:text-blue-600 transition-all shadow-sm active:scale-95 disabled:opacity-50"
                      >
                        {attendanceLoading ? (
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                        Load More Records
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="flex h-full overflow-hidden">
                {/* Fixed Left Sidebar */}
                {processedLogs.navigation.length > 0 && (
                  <div className="w-24 sm:w-28 border-r border-gray-200 bg-gray-50/30 shrink-0 hidden sm:block overflow-y-auto custom-scrollbar">
                    <div className="p-4 space-y-6">
                      {processedLogs.navigation.map((nav) => (
                        <div key={nav.year} className="space-y-1.5">
                          <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-2">{nav.year}</h4>
                          <div className="flex flex-col">
                            {nav.months.map((month) => (
                              <button
                                key={`${nav.year}-${month}`}
                                onClick={() => {
                                  const dateStr = processedLogs.monthFirstAppearance[`${nav.year}-${month}`];
                                  if (dateStr) {
                                    const element = document.getElementById(`day-${dateStr.replace(/\s/g, '-')}`);
                                    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }
                                }}
                                className="w-full text-left px-2 py-1.5 text-[10px] font-bold uppercase tracking-tight text-gray-400 hover:text-blue-600 hover:bg-white rounded-lg transition-all"
                              >
                                {month}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Scrollable History Feed */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 custom-scrollbar bg-white">
                  {processedLogs.filtered.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 bg-gray-50 rounded-3xl border border-gray-100">
                      <History className="w-8 h-8 text-gray-200 mb-2" />
                      <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest italic">No activities recorded</p>
                    </div>
                  ) : (
                    <div className="space-y-8 max-w-4xl mx-auto pb-10">
                      {Object.keys(processedLogs.groups).map((dateStr) => {
                        const date = new Date(dateStr);
                        const isToday = new Date().toDateString() === date.toDateString();

                        return (
                          <div key={dateStr} id={`day-${dateStr.replace(/\s/g, '-')}`} className="relative pl-6 sm:pl-8">
                            {/* Date Marker */}
                            <div className="absolute left-0 top-0 bottom-0 w-px bg-gray-200 flex flex-col items-center">
                              <div className="w-2 h-2 rounded-full bg-blue-600 shadow-sm z-10 -ml-px mt-2"></div>
                            </div>

                            <div className="flex items-center gap-3 mb-4">
                              <span className="text-[11px] font-black text-gray-900 uppercase tracking-tight bg-white pr-2">
                                {isToday ? 'Today' : `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}`}
                              </span>
                              <div className="h-px bg-gray-200 flex-1"></div>
                              <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest">{date.getFullYear()}</span>
                            </div>

                            <div className="space-y-2.5">
                              {processedLogs.groups[dateStr].map((log) => (
                                <div key={log.id} className="group relative">
                                  {/* Item Dot */}
                                  <div className="absolute -left-[27px] sm:-left-[35px] top-3.5 w-1.5 h-1.5 rounded-full bg-gray-300 group-hover:bg-blue-500 transition-all z-10"></div>

                                  <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 shadow-sm hover:border-blue-200 hover:shadow-md transition-all">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                                        <h5 className="text-[11px] font-black text-gray-800 uppercase tracking-tight">
                                          {log.action.replace(/_/g, ' ')}
                                        </h5>
                                      </div>
                                      <div className="text-[9px] text-gray-400 font-bold tabular-nums flex items-center gap-1.5 bg-gray-50 px-2 py-0.5 rounded-full">
                                        <Clock className="w-3 h-3" />
                                        {new Date(log.performedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </div>
                                    </div>

                                    <div className="p-2 sm:p-3 bg-gray-50/80 rounded-lg border border-gray-100 flex flex-col gap-1.5">
                                      {(() => {
                                        const details = log.details || {};
                                        if (!details || Object.keys(details).length === 0) return <p className="text-[10px] text-gray-400 italic">Standard update</p>;

                                        return (
                                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[10px]">
                                            {details.planName && (
                                              <div className="flex items-center gap-2">
                                                <span className="font-black text-gray-400 uppercase text-[8px] w-12 shrink-0">Plan</span>
                                                <span className="font-bold text-gray-700 uppercase italic truncate">{details.planName}</span>
                                              </div>
                                            )}
                                            {details.invoiceId && (
                                              <div className="flex items-center gap-2">
                                                <span className="font-black text-gray-400 uppercase text-[8px] w-12 shrink-0">Invoice</span>
                                                <span className="font-mono font-bold text-blue-600 truncate">{details.invoiceId}</span>
                                              </div>
                                            )}
                                            {details.status && (
                                              <div className="flex items-center gap-2">
                                                <span className="font-black text-gray-400 uppercase text-[8px] w-12 shrink-0">Status</span>
                                                <span className="font-black text-[9px] text-blue-700 bg-blue-100/50 px-1.5 rounded tracking-tighter">{details.status.replace(/_/g, ' ').toUpperCase()}</span>
                                              </div>
                                            )}
                                            {(details.from || details.to) && (
                                              <div className="flex items-center gap-2">
                                                <span className="font-black text-gray-400 uppercase text-[8px] w-12 shrink-0">Status</span>
                                                <span className="font-black text-[9px] text-gray-500 bg-gray-100 px-1.5 rounded tracking-tighter">{String(details.from ?? '—').replace(/_/g, ' ').toUpperCase()}</span>
                                                <svg className="w-2.5 h-2.5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5-5 5M6 7l5 5-5 5" />
                                                </svg>
                                                <span className="font-black text-[9px] text-blue-700 bg-blue-100/50 px-1.5 rounded tracking-tighter">{String(details.to ?? '—').replace(/_/g, ' ').toUpperCase()}</span>
                                              </div>
                                            )}
                                            {details.message && (
                                              <div className="col-span-full mt-1 pt-1 border-t border-gray-200/50">
                                                <p className="text-[10px] text-gray-500 leading-tight italic line-clamp-2">"{details.message}"</p>
                                              </div>
                                            )}
                                            {details.updatedFields && Object.keys(details.updatedFields).length > 0 && (
                                              <div className="col-span-full flex flex-wrap gap-2 mt-2 border-t border-gray-200/50 pt-3">
                                                {Object.entries(details.updatedFields).map(([key, value]: [string, any]) => {
                                                  const isDiff = value && typeof value === 'object' && ('old' in value || 'new' in value);
                                                  const label = key.replace(/([A-Z])/g, ' $1').toUpperCase();

                                                  return (
                                                    <div key={key} className="bg-white/80 p-2.5 rounded-xl border border-gray-100 flex flex-col gap-1.5 shadow-sm min-w-[140px] flex-1 sm:flex-none">
                                                      <div className="flex items-center gap-2">
                                                        <div className="w-1 h-3 bg-blue-400 rounded-full"></div>
                                                        <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">{label}</span>
                                                      </div>
                                                      <div className="flex items-center gap-2 pl-3">
                                                        {isDiff ? (
                                                          <div className="flex items-center gap-2.5">
                                                            <span className={`text-[10px] font-medium ${!value.old ? 'text-gray-300 italic' : 'text-gray-400 line-through decoration-gray-300'}`}>
                                                              {value.old === '' || value.old === null ? 'Empty' : String(value.old)}
                                                            </span>
                                                            <div className="flex items-center">
                                                              <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5-5 5M6 7l5 5-5 5" />
                                                              </svg>
                                                            </div>
                                                            <span className={`text-[11px] font-black ${!value.new ? 'text-gray-400 italic' : 'text-blue-600'}`}>
                                                              {value.new === '' || value.new === null ? 'Empty' : String(value.new)}
                                                            </span>
                                                          </div>
                                                        ) : (
                                                          <span className="text-[10px] font-bold text-gray-600">
                                                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                          </span>
                                                        )}
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>

                                    {log.performer && (
                                      <div className="mt-2.5 flex items-center justify-end gap-1.5 opacity-60">
                                        <div className="w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center text-[8px] font-black text-white">
                                          {(log.performer.displayName || log.performer.name || 'S').charAt(0)}
                                        </div>
                                        <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest">{log.performer.displayName || log.performer.name || 'System'}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {activeTab === 'details' && !readOnly && (
            <div className={`shrink-0 overflow-hidden transition-all duration-300 ease-out ${hasChanges || loading ? 'max-h-24 opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}>
              <div className="px-4 py-3 sm:px-6 bg-white border-t border-gray-100 flex items-center justify-between gap-3 pb-safe">
                <p className="text-xs text-amber-600 font-medium flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block"></span>
                  Unsaved changes
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all active:scale-95"
                  >
                    Discard
                  </button>
                  <button
                    type="button"
                    onClick={() => (document.querySelector('form') as HTMLFormElement)?.requestSubmit()}
                    disabled={loading || !formData.name.trim() || !formData.phoneNo.trim() || showOtpInput}
                    title={showOtpInput ? 'Complete or cancel OTP verification first' : undefined}
                    className="px-5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-all shadow-md shadow-blue-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                  >
                    {loading && (
                      <svg className="w-3.5 h-3.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    )}
                    {loading ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {
          showPauseModal && membershipToPause && (
            <PauseModal
              isOpen={showPauseModal}
              onClose={() => {
                setShowPauseModal(false);
                setMembershipToPause(null);
              }}
              membershipId={membershipToPause}
              onSuccess={() => {
                onMemberUpdated();
                setShowPauseModal(false);
                setMembershipToPause(null);
              }}
            />
          )
        }

        {
          membershipToCancel && member && (
            <DeleteMembershipDialog
              isOpen={!!membershipToCancel}
              membership={membershipToCancel}
              memberId={member.id}
              onClose={() => setMembershipToCancel(null)}
              onSuccess={() => {
                onMemberUpdated();
                setMembershipToCancel(null);
              }}
            />
          )
        }

        {
          purchaseToCancel && (
            <CancelPurchaseDialog
              isOpen={!!purchaseToCancel}
              purchase={purchaseToCancel}
              onClose={() => setPurchaseToCancel(null)}
              onSuccess={() => {
                onMemberUpdated();
                setPurchaseToCancel(null);
                // The Fetching is handled via the PurchasesTab useEffect on member.id change usually,
                // but let's signal a manual refresh if possible.
                // For now, onMemberUpdated will suffice as it often triggers parent refreshes.
              }}
            />
          )
        }

        {/* Create Purchase Modal - opened from Purchases tab */}
        {showCreatePurchaseModal && member && (
          <CreatePurchaseModal
            isOpen={showCreatePurchaseModal}
            onClose={() => { setShowCreatePurchaseModal(false); setPurchaseMode('default'); }}
            memberId={member.id}
            memberName={member.displayName || member.name}
            mode={purchaseMode}
            activePlanEndDate={purchaseMode === 'plan' ? activePlanEndDate : null}
            onSuccess={() => {
              setShowCreatePurchaseModal(false);
              setPurchaseMode('default');
              onMemberUpdated(); // Refresh member status and data
              // Refresh purchases list
              setPurchasesLoading(true);
              supabase.auth.getSession().then(({ data: { session } }) => {
                if (!session) return;
                fetch(`${API_BASE_URL}/api/gym-purchases?memberId=${member.id}`, {
                  headers: { Authorization: `Bearer ${session.access_token}` }
                }).then(r => r.json()).then(d => {
                  setGymPurchases(d.data || []);
                }).finally(() => setPurchasesLoading(false));
              });
            }}
          />
        )}
      </div>
    </div>
  );
}

// ─── ClassesTab Sub-Component ────────────────────────────────────────────────

function ClassesTab({ bookings, credits, loading, onRefresh }: {
  bookings: any[];
  credits: any[];
  loading: boolean;
  onRefresh: () => void;
}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcoming = bookings.filter(b => {
    const d = new Date(b.session?.date);
    return d >= today && b.status === 'booked';
  }).sort((a, b) => new Date(a.session.date).getTime() - new Date(b.session.date).getTime());

  const past = bookings.filter(b => {
    const d = new Date(b.session?.date);
    return d < today || b.status === 'attended' || b.status === 'no_show' || b.status === 'cancelled';
  }).sort((a, b) => new Date(b.session.date).getTime() - new Date(a.session.date).getTime());

  const totalCredits = credits.reduce((s: number, c: any) => s + c.remainingCredits, 0);

  const statusPill = (status: string) => {
    const map: Record<string, string> = {
      booked:   'bg-blue-50 text-blue-700 border-blue-100',
      attended: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      no_show:  'bg-red-50 text-red-600 border-red-100',
      cancelled:'bg-gray-100 text-gray-500 border-gray-200',
      waitlisted:'bg-amber-50 text-amber-700 border-amber-100',
    };
    const label: Record<string, string> = {
      booked: 'Booked', attended: 'Attended', no_show: 'No-show',
      cancelled: 'Cancelled', waitlisted: 'Waitlisted',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${map[status] ?? 'bg-gray-100 text-gray-500 border-gray-200'}`}>
        {label[status] ?? status}
      </span>
    );
  };

  const BookingRow = ({ b }: { b: any }) => {
    const sessionDate = b.session ? new Date(b.session.date) : null;
    const timeStr = b.session ? `${b.session.startTime} – ${b.session.endTime}` : '';
    const className = b.session?.class?.name ?? '—';
    const branch = b.session?.branch?.displayName ?? b.session?.branch?.name ?? '';
    const category = b.session?.class?.category;

    return (
      <div className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
        <div className="w-9 h-9 rounded-lg bg-blue-50 flex flex-col items-center justify-center shrink-0 border border-blue-100">
          <span className="text-[10px] font-black text-blue-600 leading-none">
            {sessionDate ? sessionDate.toLocaleDateString('en-IN', { day: 'numeric' }) : '—'}
          </span>
          <span className="text-[9px] text-blue-400 uppercase tracking-wide leading-none mt-0.5">
            {sessionDate ? sessionDate.toLocaleDateString('en-IN', { month: 'short' }) : ''}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-800 truncate">{className}</span>
            {category && (
              <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{category}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs text-gray-400">{timeStr}</span>
            {branch && <span className="text-xs text-gray-300">·</span>}
            {branch && <span className="text-xs text-gray-400">{branch}</span>}
          </div>
        </div>
        <div className="shrink-0">{statusPill(b.status)}</div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <svg className="animate-spin w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <p className="text-xs text-gray-400">Loading class history…</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Credit balance */}
      {credits.length > 0 && (
        <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-100 rounded-xl p-3 flex items-center gap-3">
          <div className="w-8 h-8 bg-violet-500 rounded-lg flex items-center justify-center shrink-0">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-xs font-black text-violet-700 uppercase tracking-wider">Class Credits</p>
            <p className="text-[11px] text-violet-500 mt-0.5">
              {totalCredits} credit{totalCredits !== 1 ? 's' : ''} remaining across {credits.length} package{credits.length !== 1 ? 's' : ''}
            </p>
          </div>
          <span className="text-2xl font-black text-violet-700">{totalCredits}</span>
        </div>
      )}

      {/* Upcoming */}
      <div>
        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Upcoming</p>
        {upcoming.length === 0 ? (
          <p className="text-xs text-gray-400 py-3">No upcoming bookings</p>
        ) : (
          <div>{upcoming.map(b => <BookingRow key={b.id} b={b} />)}</div>
        )}
      </div>

      {/* Past */}
      <div className="border-t border-gray-100 pt-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">History</p>
          <button onClick={onRefresh} className="text-[10px] text-blue-500 hover:text-blue-700 font-semibold">Refresh</button>
        </div>
        {past.length === 0 ? (
          <p className="text-xs text-gray-400 py-3">No past sessions</p>
        ) : (
          <div>{past.map(b => <BookingRow key={b.id} b={b} />)}</div>
        )}
      </div>
    </div>
  );
}

// ─── PurchasesTab Sub-Component ───────────────────────────────────────────────

function PurchasesTab({
  member,
  gymPurchases,
  setGymPurchases,
  purchasesLoading,
  setPurchasesLoading,
  onAddPurchase,
  setPurchaseToCancel
}: {
  member: any;
  gymPurchases: any[];
  setGymPurchases: (p: any[]) => void;
  purchasesLoading: boolean;
  setPurchasesLoading: (v: boolean) => void;
  onAddPurchase: () => void;
  setPurchaseToCancel: (p: any) => void;
}) {
  useEffect(() => {
    if (!member?.id) return;
    setPurchasesLoading(true);
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      fetch(`${API_BASE_URL}/api/gym-purchases?memberId=${member.id}&limit=50`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      })
        .then(r => r.json())
        .then(d => setGymPurchases(d.data || []))
        .catch(() => { })
        .finally(() => setPurchasesLoading(false));
    });
  }, [member?.id]);

  function paymentStatusBadge(status: string, purchaseStatus?: string) {
    if (purchaseStatus === 'cancelled') {
      return 'bg-red-50 text-red-700 border-red-100';
    }
    const map: Record<string, string> = {
      paid: 'bg-green-50 text-green-700 border-green-100',
      partial: 'bg-amber-50 text-amber-700 border-amber-100',
      due: 'bg-red-50 text-red-700 border-red-100'
    };
    return map[status] || map.due;
  }

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-black text-gray-900">Purchases</h3>
          <p className="text-xs text-gray-400 mt-0.5">All purchases for the member</p>
        </div>
        <button
          onClick={onAddPurchase}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-xs font-black hover:from-indigo-700 hover:to-violet-700 transition-all shadow-md shadow-indigo-200"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Purchase
        </button>
      </div>

      {purchasesLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
        </div>
      ) : gymPurchases.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center mb-4">
            <ShoppingBag className="w-8 h-8 text-gray-200" />
          </div>
          <p className="font-bold text-gray-400">No purchases yet</p>
          <p className="text-xs text-gray-300 mt-1 max-w-xs">Purchases created via the new purchase flow will appear here. Membership-only purchases remain in the Memberships tab.</p>
          <button
            onClick={onAddPurchase}
            className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-dashed border-indigo-200 text-indigo-500 text-xs font-black hover:bg-indigo-50 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create First Purchase
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {gymPurchases.map((purchase: any) => (
            <div key={purchase.id} className="rounded-2xl border border-gray-100 overflow-hidden hover:border-indigo-100 hover:shadow-sm transition-all">
              {/* Purchase header */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50/60">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-black text-gray-400">{purchase.invoiceId || 'Draft'}</span>
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border uppercase tracking-widest ${paymentStatusBadge(purchase.paymentStatus, purchase.status)}`}>
                    {purchase.status === 'cancelled' ? 'Cancelled' : purchase.paymentStatus}
                  </span>
                </div>
                <span className="text-xs text-gray-400">
                  {new Date(purchase.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              </div>

              {/* Line items */}
              <div className="px-4 py-3 space-y-2">
                {(purchase.lineItems || []).map((item: any) => (
                  <div key={item.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-black px-1.5 py-0.5 rounded border
                        ${item.itemType === 'membership' ? 'bg-blue-50 text-blue-600 border-blue-100' :
                          item.itemType === 'service' ? 'bg-violet-50 text-violet-600 border-violet-100' :
                            item.itemType === 'product' ? 'bg-amber-50 text-amber-600 border-amber-100' :
                              'bg-gray-50 text-gray-500 border-gray-100'}`}>
                        {item.itemType}
                      </span>
                      <span className="font-bold text-gray-800">{item.name}</span>
                      {item.quantity > 1 && <span className="text-gray-400">×{item.quantity}</span>}
                    </div>
                    <span className="font-bold text-gray-700">₹{(Number(item.price) * item.quantity).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>

              {/* Totals footer */}
              <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  {Number(purchase.pendingAmount) > 0 && (
                    <span className="text-amber-600 font-bold">₹{Number(purchase.pendingAmount).toLocaleString('en-IN')} pending</span>
                  )}
                  {purchase.status !== 'cancelled' && (
                    <button
                      onClick={() => setPurchaseToCancel(purchase)}
                      className="p-1 text-red-400 hover:text-red-500 rounded-lg transition-colors"
                      title="Cancel Purchase"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <span className="font-black text-gray-900 text-sm">₹{Number(purchase.totalAmount).toLocaleString('en-IN')}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── TrainersTab Sub-Component ───────────────────────────────────────────────

type TrainerAssignment = {
  id: string;
  trainerId: string;
  trainerName: string;
  role: string | null;
  notes: string | null;
  assignedAt: string;
  deletedAt: string | null;
  active: boolean;
};

function TrainersTab({ memberId, gymId, readOnly }: { memberId: string; gymId: string; readOnly: boolean }) {
  const [assignments, setAssignments] = useState<TrainerAssignment[]>([]);
  const [includeHistory, setIncludeHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [trainers, setTrainers] = useState<Array<{ userId: string; name: string }>>([]);
  const [selectedTrainerId, setSelectedTrainerId] = useState('');
  const [role, setRole] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [endingId, setEndingId] = useState<string | null>(null);

  const authHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' };
  };

  const load = async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/api/member-trainers?memberId=${memberId}&includeHistory=${includeHistory}`, { headers });
      const json = await res.json();
      if (json.success) setAssignments(json.data || []);
      else toast.error(json.error || 'Failed to load assignments');
    } catch {
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const loadTrainers = async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/api/trainers?role=trainer&status=active`, { headers });
      if (!res.ok) return;
      const json = await res.json();
      const list: Array<{ userId: string; name: string }> = (json.data || [])
        .map((t: any) => ({ userId: t.userId, name: t.displayName || t.name || '—' }))
        .filter((t: { userId: string }) => !!t.userId);
      const seen = new Set<string>();
      setTrainers(list.filter(t => seen.has(t.userId) ? false : (seen.add(t.userId), true)));
    } catch {
      // ignore
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [memberId, includeHistory]);
  useEffect(() => { loadTrainers(); }, []);

  const handleAssign = async () => {
    if (!selectedTrainerId) {
      toast.error('Pick a trainer');
      return;
    }
    setSubmitting(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/api/member-trainers`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          memberId,
          trainerId: selectedTrainerId,
          role: role.trim() || undefined,
          notes: notes.trim() || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        toast.error(json.error || 'Failed to assign trainer');
        return;
      }
      toast.success('Trainer assigned');
      setSelectedTrainerId(''); setRole(''); setNotes('');
      load();
    } catch {
      toast.error('Failed to assign trainer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnd = async (id: string) => {
    setEndingId(id);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/api/member-trainers/${id}`, { method: 'DELETE', headers });
      const json = await res.json();
      if (!res.ok || !json.success) {
        toast.error(json.error || 'Failed to end assignment');
        return;
      }
      toast.success('Assignment ended');
      load();
    } catch {
      toast.error('Failed to end assignment');
    } finally {
      setEndingId(null);
    }
  };

  const active = assignments.filter(a => a.active);
  const ended = assignments.filter(a => !a.active);

  return (
    <div className="space-y-6">
      <EntitlementCoaches memberId={memberId} readOnly={readOnly} onChanged={load} />
      {gymId && <StandingSlotsPanel gymId={gymId} memberId={memberId} readOnly={readOnly} />}
      {gymId && <PackPausesPanel gymId={gymId} memberId={memberId} readOnly={readOnly} />}
      {/* Active assignments */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-900">Currently Assigned</h3>
          <label className="flex items-center gap-2 text-xs font-medium text-gray-500 cursor-pointer">
            <input
              type="checkbox"
              checked={includeHistory}
              onChange={e => setIncludeHistory(e.target.checked)}
              className="rounded"
            />
            Show history
          </label>
        </div>
        {loading ? (
          <div className="text-xs text-gray-400 py-4">Loading…</div>
        ) : active.length === 0 ? (
          <div className="text-xs text-gray-400 py-4">No trainer assigned.</div>
        ) : (
          <div className="space-y-2">
            {active.map(a => (
              <div key={a.id} className="flex items-start justify-between p-3 rounded-xl border border-gray-100 bg-white">
                <div>
                  <div className="text-sm font-bold text-gray-900">{a.trainerName}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {a.role && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 mr-2 font-bold">{a.role}</span>}
                    Since {new Date(a.assignedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                  {a.notes && <div className="text-xs text-gray-600 mt-1">{a.notes}</div>}
                </div>
                {!readOnly && (
                  <button
                    onClick={() => handleEnd(a.id)}
                    disabled={endingId === a.id}
                    className="px-3 py-1.5 text-xs font-bold text-rose-600 hover:bg-rose-50 rounded-lg disabled:opacity-50"
                  >
                    {endingId === a.id ? 'Ending…' : 'End'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add new assignment */}
      {!readOnly && (
        <div className="p-4 rounded-xl border border-dashed border-gray-200 bg-gray-50/50">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Assign Trainer</h3>
          <div className="space-y-3">
            <select
              value={selectedTrainerId}
              onChange={e => setSelectedTrainerId(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="">Pick a trainer…</option>
              {trainers.map(t => (
                <option key={t.userId} value={t.userId}>{t.name}</option>
              ))}
            </select>
            <input
              type="text"
              value={role}
              onChange={e => setRole(e.target.value.slice(0, 30))}
              placeholder="Role (optional, e.g. PT, nutrition)"
              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value.slice(0, 1000))}
              placeholder="Notes (optional)"
              rows={2}
              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"
            />
            <button
              onClick={handleAssign}
              disabled={submitting || !selectedTrainerId}
              className="w-full py-2 bg-blue-600 text-white font-bold rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Assigning…' : 'Assign'}
            </button>
            <p className="text-[11px] text-gray-400">Multiple trainers can be assigned at the same time (different roles).</p>
          </div>
        </div>
      )}

      {/* History */}
      {includeHistory && ended.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-gray-500 mb-3">Past Assignments</h3>
          <div className="space-y-2">
            {ended.map(a => (
              <div key={a.id} className="flex items-start justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
                <div>
                  <div className="text-sm font-medium text-gray-700">{a.trainerName}</div>
                  <div className="text-[11px] text-gray-400 mt-0.5">
                    {a.role && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 mr-2 font-bold">{a.role}</span>}
                    {new Date(a.assignedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    {' → '}
                    {a.deletedAt ? new Date(a.deletedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'now'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
